import { memo, ReactNode, useCallback, useMemo } from 'react';
import { FeatureGateEnvironment, Identifiers } from '@atlaskit/feature-gate-js-client';
import { FeatureGatesInitialization } from '@atlassian/feature-gates-react';
import Loader from 'components/common/Loader';

import { STATSIG_TARGET_APP } from 'utils/constants';
import Logger from 'services/sentry/sentry';

const statsigEnvironment = () => {
  switch (process.env.REACT_APP_ENVI) {
    case 'production':
      return FeatureGateEnvironment.Production;
    case 'staging':
      return FeatureGateEnvironment.Staging;
    default:
      return FeatureGateEnvironment.Development;
  }
};

type Props = {
  children: ReactNode;
  identifiers: Identifiers;
};

const StatsigInitializer = ({ children, identifiers }: Props) => {
  const apiKey = process.env.REACT_APP_STATSIG_API_KEY || '';

  const options = useMemo(() => {
    return {
      apiKey: apiKey,
      environment: statsigEnvironment(),
      targetApp: STATSIG_TARGET_APP,
    };
  }, [apiKey]);

  const handleOnReady = useCallback(
    (err: Error | undefined): void => {
      if (err) {
        Logger.error(err, {
          data: {
            message: 'Statsig initialization failed, but FeatureGatesInitialization was loaded',
            environment: statsigEnvironment,
            identifiers,
          },
        });
      }
    },
    [identifiers],
  );

  return (
    <FeatureGatesInitialization
      options={options}
      identifiers={identifiers}
      initializingComponent={<Loader />}
      onReady={handleOnReady}
    >
      {children}
    </FeatureGatesInitialization>
  );
};

export default memo(StatsigInitializer);
